// PipCastControls — 懸浮窗（PiP）+ 投屏（Remote Playback）按鈕列（APP#18 web）。
//
// Live 與回放共用。包 usePip + useRemotePlayback：
//   - 懸浮窗鈕：瀏覽器支援且來源就緒才顯示/可用；aria-pressed 反映開關態。
//   - 投屏鈕：Remote Playback 支援且偵測到可投裝置時顯示；prompt() 開裝置選單。
// 不支援的能力直接隱藏該鈕（不留 disabled 殘鈕擾人）。

import { useTranslation } from 'react-i18next';
import { usePip } from './usePip';
import { useRemotePlayback } from './useRemotePlayback';
import './pip.css';

interface Props {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  /** 來源是否就緒（live=streaming 且有串流；回放=有 src）。 */
  ready: boolean;
  className?: string;
}

export function PipCastControls({ videoRef, ready, className }: Props) {
  const { t } = useTranslation();
  const pip = usePip(videoRef, ready);
  const cast = useRemotePlayback(videoRef, ready);

  // 兩者皆不支援 → 不渲染整列。
  if (!pip.supported && !cast.supported) return null;

  return (
    <div className={`pip-controls${className ? ` ${className}` : ''}`}>
      {pip.supported && (
        <button
          type="button"
          className={pip.active ? 'btn-primary pip-btn' : 'btn-ghost pip-btn'}
          disabled={!ready}
          onClick={() => void pip.toggle()}
          aria-label={pip.active ? t('pip.exitPipAria') : t('pip.enterPipAria')}
          aria-pressed={pip.active}
          data-testid="pip-toggle"
        >
          <span aria-hidden="true">⧉</span> {pip.active ? t('pip.exitPip') : t('pip.pip')}
        </button>
      )}

      {/* 投屏：支援且偵測到可投裝置才顯示（部分平台僅 prompt 時才知 → supported 即顯示） */}
      {cast.supported && (cast.available || cast.connected) && (
        <button
          type="button"
          className={cast.connected ? 'btn-primary pip-btn' : 'btn-ghost pip-btn'}
          disabled={!ready}
          onClick={() => void cast.prompt()}
          aria-label={t('pip.castAria')}
          aria-pressed={cast.connected}
          data-testid="cast-toggle"
        >
          <span aria-hidden="true">📺</span> {cast.connected ? t('pip.casting') : t('pip.cast')}
        </button>
      )}
    </div>
  );
}
